import React, { useState, useEffect, useCallback, memo } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useGetExistingOrderItemProductReviewQuery } from '../../../redux/apis/endpoints/reviewApi';
import ReviewPhotoUploader from './ReviewPhotoUploader';

const OrderItemReviewSection = memo(({ item, data, onChange }) => {
  const { rating, comment, pictures } = data;
  
  // Check for existing review for this order item
  const { data: existingReview, isLoading: isLoadingExisting } = useGetExistingOrderItemProductReviewQuery({
    orderItemId: item.id
  });

  // Initialize form with existing review data when found
  useEffect(() => {
    if (existingReview && !data.initialized) {
      console.log(`Loading existing review for order item ${item.id}:`, existingReview);
      onChange(item.id, {
        rating: existingReview.rating || 5,
        comment: existingReview.comment || '',
        pictures: existingReview.pictures || [],
        initialized: true,
        existingReviewId: existingReview.id
      });
    }
  }, [existingReview, item.id, data.initialized, onChange]);

  // Memoize handlers to prevent unnecessary re-renders
  const handleRating = useCallback((r) => {
    onChange(item.id, { rating: r });
  }, [item.id, onChange]);

  const handleComment = useCallback((e) => {
    const val = e.target.value;
    onChange(item.id, { comment: val });
  }, [item.id, onChange]);

  const handlePhotos = useCallback((pics) => {
    onChange(item.id, { pictures: pics });
  }, [item.id, onChange]);

  const isExistingReview = !!data.existingReviewId;

  return (
    <div className="border border-gray-200 rounded-lg p-4 mb-8" key={item.id}>
      <div className="flex items-center gap-4 mb-4">
        {item.product?.photos?.[0]?.url ? (
          <img src={item.product.photos[0].url} alt={item.product.name} className="w-20 h-20 rounded object-cover" />
        ) : (
          <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center">
            <span className="text-gray-400 text-xs">No Image</span>
          </div>
        )}
        <div className="flex-1">
          <h3 className="font-semibold text-lg">{item.product?.name}</h3>
          {item.product?.brand && <p className="text-sm text-gray-600">Brand: {item.product.brand}</p>}
          {item.product?.price && <p className="text-sm font-medium">${Number(item.product.price).toFixed(2)}</p>}
          {isLoadingExisting && <p className="text-xs text-blue-600">Loading existing review...</p>}
          {isExistingReview && <p className="text-xs text-green-600">✓ Editing existing review</p>}
        </div>
      </div>

      {/* Rating */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
        <div className="flex items-center">
          {[1,2,3,4,5].map(star => (
            <button key={star} type="button" className="p-1 focus:outline-none hover:scale-110 transition-transform" onClick={() => handleRating(star)}>
              <StarIcon className={`h-8 w-8 ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`} />
            </button>
          ))}
          <span className="ml-2 text-sm text-gray-600">{rating} {rating===1?'Star':'Stars'}</span>
        </div>
      </div>

      {/* Comment */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Review</label>
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className={`text-xs ${comment && comment.trim().length < 10 && comment.trim().length > 0 ? 'text-red-500' : 'text-gray-500'}`}>
              {(comment?.length || 0)}/1000 characters {comment && comment.trim().length < 10 && comment.trim().length > 0 && '(minimum 10 characters required)'}
            </span>
          </div>
          <textarea
            rows={6}
            value={comment || ''}
            onChange={handleComment}
            placeholder="Tell others about your experience with this product"
            maxLength={1000}
            className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-teal focus:border-brand-teal resize-vertical ${
              comment && comment.trim().length < 10 && comment.trim().length > 0 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
            }`}
          />
          {comment && comment.trim().length < 10 && comment.trim().length > 0 && (
            <p className="mt-2 text-sm text-red-600 flex items-center">
              <ExclamationCircleIcon className="h-4 w-4 mr-1" />
              Please write at least 10 characters for your review
            </p>
          )}
        </div>
      </div>

      {/* Photos */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Photos (optional)</label>
        <ReviewPhotoUploader
          onUploadPics={handlePhotos}
          error={null}
          initialPhotos={pictures || []}
        />
      </div>
    </div>
  );
});

OrderItemReviewSection.displayName = 'OrderItemReviewSection';

export default OrderItemReviewSection; 